<template>
    <filters-wrapper :has-active-filters="props.hasActiveFilters" @submit="emit('submit')" @reset="emit('reset')">
        <template v-slot:panel>
            <FilterStatusToggle v-model="filters.active" @update:model-value="emit('submit')" />
        </template>
        <v-form class="table-filters">
            <v-row dense>
                <v-col cols="12" md="3" sm="6">
                    <v-text-field
                        v-model="filters.name"
                        :label="$t('name')"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="name"
                    />
                </v-col>
                <v-col cols="12" md="3" sm="6">
                    <v-autocomplete
                        v-model="filters.countryId"
                        :items="useCountries"
                        :label="$t('country')"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="country-id"
                    />
                </v-col>
                <v-col cols="12" md="3" sm="6">
                    <v-autocomplete
                        v-model="filters.cityId"
                        :items="cities"
                        :label="$t('city')"
                        :disabled="isCitiesLoading || !cities.length"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="city-id"
                    />
                </v-col>
            </v-row>
        </v-form>
    </filters-wrapper>
</template>

<script setup lang="ts">
    import type {SelectOptionType} from '~/utils/types/types'
    import {useCountries} from '~/composables/form-helpers'
    import type {OfficeFiltersType} from '~/utils/types/account/offices/types'

    const props = defineProps<{
        hasActiveFilters: boolean
    }>()

    const filters = defineModel<OfficeFiltersType>()

    const emit = defineEmits<{
        (e: 'submit' | 'reset'): void
    }>()

    const cities = ref<SelectOptionType[]>([])
    const isCitiesLoading = ref<boolean>(true)

    const app = useNuxtApp()

    watch(
        () => filters.value.countryId,
        async (): Promise<void> => {
            filters.value.cityId = null
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            filters.value.countryId ? await loadCities() : (cities.value = [])
        },
        {deep: true}
    )

    async function loadCities(): Promise<void> {
        try {
            isCitiesLoading.value = true
            const rs = await app.$api.locations.get({
                limitCities: -1,
                orderBy: 'POPULARITY',
                countryId: filters.value.countryId,
            })
            if (rs.cities) {
                cities.value = rs.cities.map(({id, name}) => {
                    return {title: name, value: id}
                })
            }
        } catch (e) {
            cities.value = []
        } finally {
            isCitiesLoading.value = false
        }
    }
</script>
